import { useEffect, useState, useCallback, useRef } from "react";

import { getPaginatedUsersList } from "../services/userService";

interface IuseFetchUsers {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  page: number;
  pSize: number;
  searchTerm?: string;
  email?: string;
  userFilterIds?: string;
  isActive?: string;
}
const useFetchUsersList = ({
  setIsLoading,
  page,
  pSize,
  searchTerm,
  email,
  userFilterIds,
  isActive = "true",
}: IuseFetchUsers) => {
  const [usersData, setUsersData] = useState<any>({});
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(
    async (searchQuery?: string) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const result = await getPaginatedUsersList({
          page,
          pSize,
          searchTerm: searchQuery || searchTerm,
          email,
          userFilterIds,
          isActive,
          signal: abortControllerRef.current.signal,
        });
        setUsersData(result);
      } catch (err: any) {
        // Don't update state if request was aborted
        if (err.name !== "AbortError") {
          setUsersData({});
        }
      } finally {
        setIsLoading(false);
      }
    },
    [page, pSize, searchTerm, email, userFilterIds, isActive, setIsLoading]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [usersData, fetchData];
};
export default useFetchUsersList;
